/**
 * Browser auth helper: session cookie + CSRF header.
 * Never stores tokens, CSRF secrets, or session IDs in localStorage.
 */

export function clearFakeAuthStorage() {
  try {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  } catch {
    /* ignore */
  }
}

export async function fetchCsrfToken() {
  const res = await fetch('/api/auth/csrf', {
    method: 'GET',
    credentials: 'include'
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.csrfToken) {
    const err = new Error(data.message || 'Authentication is temporarily unavailable.');
    err.code = data.code || 'AUTH_UNAVAILABLE';
    throw err;
  }
  return data.csrfToken;
}

export function authJsonHeaders(csrfToken) {
  const headers = {
    'Content-Type': 'application/json'
  };
  if (csrfToken) {
    headers['X-CSRF-Token'] = csrfToken;
  }
  return headers;
}

/**
 * Authoritative session lookup. Distinguishes 401, 403, and transport errors.
 */
export async function fetchSession() {
  clearFakeAuthStorage();
  try {
    const res = await fetch('/api/auth/me', {
      method: 'GET',
      credentials: 'include'
    });
    const data = await res.json().catch(() => ({}));
    if (res.status === 401) {
      return {
        authenticated: false,
        status: 401,
        code: data.code || 'UNAUTHENTICATED',
        message: data.message || 'Sign in required.'
      };
    }
    if (res.status === 403) {
      return {
        authenticated: false,
        status: 403,
        code: data.code || 'PENDING_OR_INACTIVE',
        message: data.message || 'This account cannot use that feature yet.',
        user: data.user || null
      };
    }
    if (!res.ok || !data.user) {
      return {
        error: true,
        status: res.status,
        message: data.message || 'Unable to load account.'
      };
    }
    return {
      authenticated: true,
      status: 200,
      user: data.user,
      csrfToken: data.csrfToken || null
    };
  } catch {
    return {
      error: true,
      status: 0,
      network: true,
      message: 'Network error. Please try again.'
    };
  }
}

export async function fetchCurrentUser() {
  const session = await fetchSession();
  if (!session.authenticated) return null;
  return { user: session.user, csrfToken: session.csrfToken };
}

export async function signOut() {
  let csrfToken = null;
  try {
    csrfToken = await fetchCsrfToken();
  } catch {
    csrfToken = null;
  }
  await fetch('/api/auth/logout', {
    method: 'POST',
    credentials: 'include',
    headers: authJsonHeaders(csrfToken)
  });
  clearFakeAuthStorage();
}
